import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface ChipCarouselProps<T extends string | number> {
  items: T[];
  selected: T | null;
  onSelect: (item: T) => void;
  getLabel: (item: T) => string;
  previousLabel: string;
  nextLabel: string;
  className?: string;
}

/**
 * Horizontal, scrollable row of selectable pills — the one place the app already solved "many
 * of these, pick one": fade edges plus arrows when it overflows, auto-scrolled to the selection.
 * First built for the session rail on the Notes page; kept generic (a plain item + label, no
 * domain type) so the prepare-session briefing's own session picker is the same component, not a
 * lookalike that drifts from it later.
 */
function ChipCarousel<T extends string | number>({
  items,
  selected,
  onSelect,
  getLabel,
  previousLabel,
  nextLabel,
  className = '',
}: ChipCarouselProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const syncArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setCanScrollLeft(track.scrollLeft > 1);
    setCanScrollRight(track.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    syncArrows();
    const observer = new ResizeObserver(syncArrows);
    observer.observe(track);
    return () => observer.disconnect();
  }, [syncArrows, items]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || selected === null) return;
    const chip = track.querySelector<HTMLElement>(`[data-chip="${String(selected)}"]`);
    if (!chip) return;
    // Centers the chip between the two arrows rather than just nudging it into view — `getBoundingClientRect`
    // instead of `offsetLeft`, since the track itself isn't a positioned ancestor and `offsetLeft`
    // would resolve against the wrapper around it instead. Clamped to the real scroll range, so
    // the first and last chips land as far as they can rather than trying to centre past the
    // edge — that's also the one case where an arrow has nothing left to scroll to and disappears.
    const trackRect = track.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const chipOffset = chipRect.left - trackRect.left + track.scrollLeft;
    const target = chipOffset - (track.clientWidth - chipRect.width) / 2;
    const maxScroll = track.scrollWidth - track.clientWidth;
    track.scrollTo({ left: Math.max(0, Math.min(target, maxScroll)), behavior: 'smooth' });
  }, [selected, items]);

  const step = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <div className={`relative ${className}`}>
      <div
        ref={trackRef}
        onScroll={syncArrows}
        // `overflow-x-auto` alone makes the browser treat `overflow-y` as `auto` too, not the
        // `visible` it defaults to — without saying so explicitly, that silently clips a pill's
        // bottom edge (rounded corner, focus ring) by a pixel or two. Horizontal scroll is the
        // only kind this row is meant to have.
        className="flex gap-2 overflow-x-auto overflow-y-visible py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item) => {
          const active = selected === item;
          return (
            <button
              key={String(item)}
              data-chip={String(item)}
              type="button"
              onClick={() => onSelect(item)}
              className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition active:scale-[0.98] ${
                active
                  ? 'bg-brand-600 text-white'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {getLabel(item)}
            </button>
          );
        })}
      </div>

      {canScrollLeft && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center bg-gradient-to-r from-white via-white to-transparent pr-6">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label={previousLabel}
            className="pointer-events-auto hidden rounded-full border border-slate-200 bg-white p-1 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-700 [@media(hover:hover)]:inline-flex"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {canScrollRight && (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center bg-gradient-to-l from-white via-white to-transparent pl-6">
          <button
            type="button"
            onClick={() => step(1)}
            aria-label={nextLabel}
            className="pointer-events-auto hidden rounded-full border border-slate-200 bg-white p-1 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-700 [@media(hover:hover)]:inline-flex"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default ChipCarousel;
